import { Link } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { useSeo } from "../hooks/useSeo";
import { IMG, INSTAGRAM } from "../data/content";
import { ArrowRight } from "lucide-react";

const MILESTONES = [
  { n: "1969", label: "Gründung durch Emil Blum" },
  { n: "1973", label: "Erster Umzug – größeres Gebäude mit Lager" },
  { n: "1993", label: "Übergabe an Sohn Michael Blum" },
  { n: "2003", label: "Umzug ins Gewerbegebiet – 850 m² Ausstellung, Lager und Verwaltung in Mittelkalbach" },
  { n: "2019", label: "Übergabe an Sohn Konstantin Blum – Vater und Sohn leiten den Betrieb gemeinsam als Geschäftsführer" },
  { n: "2025", label: "Bereits 25 junge Erwachsene haben die Ausbildung zum Parkett-/Bodenleger oder zur Fachkraft für Büromanagement absolviert" },
];

const NEXT_PAGES = [
  { title: "Ausstellung", desc: "Über 150 m² Verkaufs- und Ausstellungsfläche in Mittelkalbach.", to: "/unternehmen/ausstellung" },
  { title: "Ausbildung, Jobs & Karriere", desc: "Werde Teil unseres Teams – vom Lehrling bis zum Meister.", to: "/unternehmen/karriere" },
  { title: "50 Jahre Fussboden Blum", desc: "Ein Rückblick auf ein halbes Jahrhundert Handwerk.", to: "/unternehmen/50-jahre" },
  { title: "Neuigkeiten & Aktuelles", desc: "Was bei uns gerade passiert – Projekte, Messen, Termine – finden Sie auf unserer Instagram-Seite.", href: INSTAGRAM.url },
  { title: "Auszeichnungen", desc: "Parkett Star, TÜV Rheinland, Boden Zukunft Award – anerkannte Qualität.", to: "/unternehmen/auszeichnungen" },
];

const UnternehmenHero = () => (
  <section data-testid="unternehmen-hero" className="relative min-h-[68vh] flex items-end overflow-hidden">
    <img
      src={IMG.teamNormal}
      alt="Das Team von Fussboden Blum vor dem Firmengebäude in Mittelkalbach"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[#2B2B30]/95 via-[#2B2B30]/55 to-[#2B2B30]/30" />
    <div className="container-blum relative z-10 pb-16 pt-40">
      <Reveal>
        <p data-testid="unternehmen-hero-script" className="font-script text-3xl lg:text-4xl text-[#D7C4B1]">
          Drei Generationen Bodenständigkeit
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight mt-3 max-w-3xl">
          Lernen Sie uns kennen
        </h1>
        <p className="text-lg text-white/85 mt-5 max-w-2xl leading-relaxed">
          Vom Wohnhaus mit Garage zu einem der größten Parkett- und
          Bodenbelagsfachbetriebe der Region – Familienhandwerk seit 1969.
        </p>
      </Reveal>
    </div>
  </section>
);

const FUEHRUNG = [
  {
    name: "Michael Blum",
    role: "Geschäftsführer · Parkettlegermeister",
    gen: "Zweite Generation",
    img1: IMG.gfMichael1,
    img2: IMG.gfMichael2,
    testid: "fuehrung-card-michael",
  },
  {
    name: "Konstantin Blum",
    role: "Geschäftsführer · Parkettlegermeister, Handwerksmanager B.A. · ö.b.u.v. Sachverständiger und Raumakustikberater",
    gen: "Dritte Generation",
    img1: IMG.gfKonstantin1,
    img2: IMG.gfKonstantin2,
    testid: "fuehrung-card-konstantin",
  },
];

const Geschaeftsfuehrung = () => (
  <section data-testid="unternehmen-fuehrung" className="py-16 lg:py-24">
    <div className="container-blum">
      <div className="max-w-2xl mb-12">
        <Reveal><p className="overline">Die Geschäftsführung</p></Reveal>
        <Reveal delay={0.05}>
          <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mt-4 leading-tight">
            Zwei Generationen – persönlich für Sie da.
          </h2>
        </Reveal>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl">
        {FUEHRUNG.map((p, i) => (
          <Reveal key={p.name} delay={i * 0.08}>
            <div data-testid={p.testid} className="group relative overflow-hidden aspect-[4/5]">
              <img
                src={p.img1}
                alt={`${p.name} – Geschäftsführer von Fussboden Blum`}
                className="absolute inset-0 w-full h-full object-cover object-top transition-[opacity,transform] duration-700 ease-out group-hover:opacity-0 group-hover:scale-105"
              />
              <img
                src={p.img2}
                alt={`${p.name} – Geschäftsführer von Fussboden Blum, zweites Porträt`}
                className="absolute inset-0 w-full h-full object-cover object-top opacity-0 scale-105 transition-[opacity,transform] duration-700 ease-out group-hover:opacity-100 group-hover:scale-100"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2B2B30]/85 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-0 left-0 p-6 lg:p-8 pointer-events-none">
                <p className="font-script text-2xl text-[#D7C4B1]">{p.gen}</p>
                <h3 className="font-display text-2xl font-bold text-white mt-1">{p.name}</h3>
                <p className="text-white/80 text-sm mt-1">{p.role}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Geschichte = () => (
  <section data-testid="unternehmen-geschichte" className="py-20 lg:py-28">
    <div className="container-blum">
      <div className="max-w-3xl">
        <Reveal><p className="overline">Unsere Geschichte</p></Reveal>
        <Reveal delay={0.05}>
          <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mt-4 leading-tight">
            Fußboden Blum – Erfahrung und Kompetenz seit 1969
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="text-[#6B6B73] text-lg leading-relaxed mt-6 space-y-5">
            <p>
              Das Familienunternehmen Fussboden Blum existiert seit 1969. Firmengründer Emil Blum –
              der Vater des heutigen Geschäftsführers – hatte von Anfang an großen Wert auf die
              Qualität seiner Arbeit und einen menschlichen und respektvollen Umgang mit Kunden und
              Geschäftspartnern gelegt. Diese Einstellung half ihm, seine Marktposition im Laufe der
              Jahre zu festigen und weiter auszubauen: Bereits 1973 konnte die Firma Fussboden Blum
              von ihrem ursprünglichen Sitz – einem Wohnhaus mit Garage – in ein größeres Gebäude mit
              angeschlossenem Lager umziehen.
            </p>
            <p>
              1993 übergab Emil Blum das Unternehmen an seinen Sohn Michael. Durch den
              unternehmerischen Geist und das erfolgreiche Wirken von Michael Blum wechselte
              Fussboden Blum nach zehn Jahren erneut den Standort und zog ins Gewerbegebiet von
              Mittelkalbach.
            </p>
            <p>
              Seitdem verfügt das Unternehmen über einen Gebäudekomplex mit einer Fläche von
              insgesamt 850 Quadratmetern, der eine umfangreiche Ausstellung mit angeschlossener
              Verwaltung und Lagerhalle beherbergt.
            </p>
          </div>
        </Reveal>
      </div>

      {/* Milestones */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-16">
        {MILESTONES.map((m, i) => (
          <Reveal key={m.n} delay={i * 0.06}>
            <div data-testid={`milestone-${i}`} className="p-6 bg-[#F9F8F6] border border-[#E5E0D8] h-full">
              <span className="font-display text-3xl lg:text-4xl font-bold text-[#D7C4B1]">{m.n}</span>
              <p className="text-[#2B2B30] font-medium text-sm mt-3 leading-snug">{m.label}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.1}>
        <figure className="mt-16">
          <img
            src={IMG.firmengebaeude}
            alt="Firmengebäude von Fussboden Blum in Mittelkalbach mit Ausstellungs-Stele"
            data-testid="unternehmen-gebaeude"
            className="w-full aspect-[21/9] object-cover"
          />
          <figcaption className="text-sm text-[#6B6B73] mt-3 flex items-center gap-3">
            <span className="w-6 h-px bg-[#D32F2F] shrink-0" aria-hidden="true" />
            Unser Standort in Kalbach
          </figcaption>
        </figure>
      </Reveal>
    </div>
  </section>
);

const Partner = () => (
  <section data-testid="unternehmen-partner" className="py-20 lg:py-28 bg-white border-y border-[#E5E0D8]">
    <div className="container-blum max-w-3xl">
      <Reveal><p className="overline">Unser Anspruch</p></Reveal>
      <Reveal delay={0.05}>
        <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mt-4 leading-tight">
          Fussboden Blum als kompetenter und vertrauensvoller Partner
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="text-[#6B6B73] text-lg leading-relaxed mt-6 space-y-5">
          <p>
            Fussboden Blum hat sich seitdem konsequent an den Bedürfnissen der Kunden orientiert und
            immer weiter entwickelt. Geschäftsführer Michael Blum und sein Team zeichnen sich durch
            großes handwerkliches Können und einen hohen Anspruch an die Qualität der eigenen Arbeit
            aus. Der Erfolg dieser Ambition wurde schließlich auch vom TÜV Rheinland bestätigt, der
            das Unternehmen Fussboden Blum im Jahr 2012 als ersten Betrieb in Hessen mit dem
            TÜV-Siegel „Fachbetrieb für Fußbodentechnik" ausgezeichnet hat.
          </p>
          <p>
            Die Firma Fussboden Blum ist ein kompetenter Partner in allen Fragen der Bodengestaltung.
            Von der individuellen Beratung über die Planung bis hin zur Ausführung in allen Details
            realisiert das Unternehmen die Wünsche von Architekten und Bauherren.
          </p>
          <p>
            Nicht nur Lieferung und Verlegung von neuen Produkten und Materialien werden ausgeführt,
            auch Serviceleistungen von der Reinigung über die Pflege bis hin zur Renovierung der
            Bodenbeläge gehören zum Angebot der Firma Blum.
          </p>
          <p>
            Das Unternehmen Fussboden Blum GmbH ist eine der größten Parkett- und Bodenbelagsfirmen
            im Landkreis Fulda. Sie präsentiert auf über 150 Quadratmetern Verkaufs- und
            Ausstellungsfläche den Firmen- und Privatkunden, Architekten und Planern eine große
            Auswahl an Produkten. Fußboden Blum konzentriert sich auf die Verlegung von Massiv- und
            Fertigparkett, Teppichböden, PVC, Design- und Linoleumbelägen. Auch die Verlegung von
            Kautschuk, Kork und Laminat sowie die Ausführung von Unterbodenarbeiten gehören zum
            Leistungsspektrum.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

const NextPages = () => (
  <section data-testid="unternehmen-next" className="py-20 lg:py-28">
    <div className="container-blum">
      <div className="max-w-2xl mb-12">
        <Reveal><p className="overline">Mehr über uns</p></Reveal>
        <Reveal delay={0.05}>
          <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mt-4 leading-tight">
            Entdecken Sie weitere Einblicke
          </h2>
        </Reveal>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {NEXT_PAGES.map((p, i) => {
          const inner = (
            <>
              <div>
                {!p.to && !p.href && (
                  <span className="inline-block text-xs font-display uppercase tracking-[0.15em] text-[#D32F2F] border border-[#D32F2F]/30 px-2.5 py-1 mb-4">
                    In Vorbereitung
                  </span>
                )}
                <h3 className="font-display text-xl lg:text-2xl font-semibold text-[#2B2B30]">{p.title}</h3>
                <p className="text-[#6B6B73] text-sm leading-relaxed mt-2">{p.desc}</p>
              </div>
              <ArrowRight className="w-6 h-6 text-[#D7C4B1] shrink-0 mt-1 transition-transform group-hover:translate-x-1" />
            </>
          );
          const cls = "group relative p-8 border border-[#E5E0D8] bg-white h-full flex items-start justify-between gap-6 transition-colors hover:border-[#2B2B30]";
          return (
            <Reveal key={p.title} delay={i * 0.06}>
              {p.href ? (
                <a href={p.href} target="_blank" rel="noopener noreferrer" data-testid={`next-page-${i}`} className={cls}>{inner}</a>
              ) : p.to ? (
                <Link to={p.to} data-testid={`next-page-${i}`} className={cls}>{inner}</Link>
              ) : (
                <div data-testid={`next-page-${i}`} className={cls}>{inner}</div>
              )}
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

export default function Unternehmen() {
  useSeo(
    "Fußboden Blum – Erfahrung und Kompetenz seit 1969",
    "Unternehmen Fussboden Blum stellt sich vor - lernen Sie uns kennen!"
  );

  return (
    <div data-testid="unternehmen-page">
      <Navbar />
      <main>
        <UnternehmenHero />
        <Geschaeftsfuehrung />
        <Geschichte />
        <Partner />
        <NextPages />
      </main>
      <Footer />
    </div>
  );
}
